import {useLazyQuery} from "@apollo/client";
import {
    CustomerServiceOrderListResponse,
    GET_CUSTOMER_SERVICE_PARCELS_LITE,
} from "../graphql/queries/getCustomerServiceParcelsLite";
import {useEffect, useState} from "react";
import {OrderType} from "../../../../graphql/types/OrderType";
import {CustomerServiceOrderFiltersType} from "../graphql/types/CustomerServiceOrderFiltersType";

export interface CustomerServiceParcelQueryResult {
    parcelData: OrderType[];
    loading: boolean;
    refetch: (variables?: any) => Promise<any>;
    totalElements: number;
    accessiblePages: number;
    buildQueryVariables: () => any;
}

export const useCustomerServiceParcelQuery = (
    filters: CustomerServiceOrderFiltersType,
    currentPage: number,
    pageSize: number
): CustomerServiceParcelQueryResult => {
    const [parcelData, setParcelData] = useState<OrderType[]>([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [accessiblePages, setAccessiblePages] = useState(0);

    const [executeQuery, { data, loading: queryLoading }] = useLazyQuery<CustomerServiceOrderListResponse>(
        GET_CUSTOMER_SERVICE_PARCELS_LITE
    );

    const buildQueryVariables = () => {
        const queryFilters: any = {
            trackingNumbers: filters.trackingNumbers || undefined,
            recipientPhone: filters.recipientPhone || undefined,
        };

        if (!queryFilters.trackingNumbers) {
            delete queryFilters.trackingNumbers;
        }
        if (!queryFilters.recipientPhone) {
            delete queryFilters.recipientPhone;
        }

        return {
            filters: queryFilters,
            pageRequest: {
                page: currentPage,
                size: pageSize,
                sortBy: "orderTime",
                sortDirection: "DESC"
            }
        };
    };

    useEffect(() => {
        const hasQueryCondition = filters.trackingNumbers?.trim() || filters.recipientPhone?.trim();

        if (!hasQueryCondition) {
            setParcelData([]);
            setTotal(0);
            setAccessiblePages(0);
            setLoading(false);
            return;
        }

        setLoading(true);
        executeQuery({
            variables: buildQueryVariables()
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, currentPage, pageSize]);

    useEffect(() => {
        // 当查询完成时（无论是否有数据），都应该更新加载状态
        if (!queryLoading) {
            setLoading(false);
        }

        if (data?.getOrderListingForCentral) {
            setParcelData(data.getOrderListingForCentral.content || []);
            setTotal(data.getOrderListingForCentral.totalElements || 0);
            setAccessiblePages(data.getOrderListingForCentral.accessiblePages || 0);
        }
    }, [data, queryLoading]);

    const refetch = async (variables?: any) => {
        const hasQueryCondition = filters.trackingNumbers?.trim() || filters.recipientPhone?.trim();

        if (!hasQueryCondition) {
            setParcelData([]);
            setTotal(0);
            setAccessiblePages(0);
            setLoading(false);
            return Promise.resolve({ data: null });
        }

        setLoading(true);
        const result = await executeQuery({
            variables: variables || buildQueryVariables()
        });
        return result;
    };

    return {
        parcelData,
        loading: loading || queryLoading,
        refetch,
        totalElements: total,
        accessiblePages,
        buildQueryVariables
    };
};

